<template>
  <div class="call-duration-filter">
    <BPopover
      v-model="visible"
      placement="bottom-start"
      trigger="click"
      full
      :width="260"
      @show="handleShow"
    >
      <template #reference>
        <BInput
          :model-value="displayValue"
          :placeholder="$t('vaApp.callList.filter.callDuration.placeholder')"
          clearable
          @clear="handleClear"
          @keydown.prevent
        >
          <template #prefix>
            <span class="call-duration-input-icon">
              <BIcon>schedule</BIcon>
            </span>
          </template>
        </BInput>
      </template>
      <div class="call-duration-popover">
        <div class="call-duration-popover-header fw-bold">
          {{ $t('vaApp.callList.filter.callDuration.title') }}
        </div>
        <div class="call-duration-popover-body">
          <div class="number-input-container">
            <BInput
              v-model="inputMin"
              type="number"
              :min="0"
              placeholder="0"
            />
            <span class="number-input-assist-text">{{ $t('vaApp.callList.filter.callDuration.minSuffix') }}</span>
          </div>
          <div class="number-input-container mt-200">
            <BInput
              v-model="inputMax"
              type="number"
              :min="0"
              placeholder="0"
            />
            <span class="number-input-assist-text">{{ $t('vaApp.callList.filter.callDuration.maxSuffix') }}</span>
          </div>
          <p
            v-if="errorMessage"
            class="error-text mt-200"
          >
            {{ errorMessage }}
          </p>
        </div>
        <div class="call-duration-popover-footer">
          <BLayout
            align-center
            justify-center
          >
            <BBtn
              class="clear-button"
              size="mini"
              text
              @click="handleClear"
            >
              {{ $t('general.clear') }}
            </BBtn>
            <BBtn
              class="ok-button"
              size="mini"
              type="primary"
              :disabled="!!errorMessage"
              @click="handleApply"
            >
              {{ $t('general.ok') }}
            </BBtn>
          </BLayout>
        </div>
      </div>
    </BPopover>
  </div>
</template>

<script setup lang="ts">
import { computed, defineModel, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const durationFrom = defineModel<number | undefined>('durationFrom');
const durationTo = defineModel<number | undefined>('durationTo');

const { t } = useI18n();

const visible = ref(false);
const inputMin = ref<string | number | null>(null);
const inputMax = ref<string | number | null>(null);

// 60秒以上は「M分S秒」表記に変換して表示する
const formatSecondsForDisplay = (seconds: number): string => {
  if (seconds < 60) return `${seconds}秒`;
  const minutes = Math.floor(seconds / 60);
  const remainderSeconds = seconds % 60;
  return remainderSeconds === 0 ? `${minutes}分` : `${minutes}分${remainderSeconds}秒`;
};

const displayValue = computed(() => {
  const min = durationFrom.value;
  const max = durationTo.value;
  if (min == null && max == null) return '';
  if (min != null && max != null) {
    return `${formatSecondsForDisplay(min)}〜${formatSecondsForDisplay(max)}`;
  }
  if (min != null) {
    return `${formatSecondsForDisplay(min)}〜`;
  }
  return `〜${formatSecondsForDisplay(max)}`;
});

const toNumberOrUndefined = (value: string | number | null): number | undefined => {
  if (value === null || value === '') return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

const isValidNonNegativeInteger = (value: string | number | null): boolean => {
  if (value === null || value === '') return true;
  const num = Number(value);
  return Number.isInteger(num) && num >= 0;
};

const errorMessage = computed(() => {
  if (!isValidNonNegativeInteger(inputMin.value) || !isValidNonNegativeInteger(inputMax.value)) {
    return t('vaApp.callList.filter.callDuration.invalidNumberError');
  }
  const min = toNumberOrUndefined(inputMin.value);
  const max = toNumberOrUndefined(inputMax.value);
  if (min != null && max != null && min > max) {
    return t('vaApp.callList.filter.callDuration.minMaxOrderError');
  }
  return '';
});

const handleShow = () => {
  inputMin.value = durationFrom.value ?? null;
  inputMax.value = durationTo.value ?? null;
};

const handleClear = () => {
  inputMin.value = null;
  inputMax.value = null;
  durationFrom.value = undefined;
  durationTo.value = undefined;
  visible.value = false;
};

const handleApply = () => {
  durationFrom.value = toNumberOrUndefined(inputMin.value);
  durationTo.value = toNumberOrUndefined(inputMax.value);
  visible.value = false;
};
</script>

<style lang="scss" scoped>
.call-duration-input-icon {
  :deep(.b-icon) {
    color: $textcolor-light;
  }
}

.call-duration-popover-header {
  border-bottom: 1px solid $bdcolor-base;
  padding: $basespace-200 $basespace-500;
}

.call-duration-popover-body {
  padding: $basespace-300 $basespace-500;
}

.number-input-container {
  display: flex;
  align-items: center;

  :deep(.b-input-wrapper) {
    flex: 5;
    max-width: 130px;

    input[type='number'] {
      appearance: textfield;
      -moz-appearance: textfield;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        appearance: none;
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }

  .number-input-assist-text {
    flex: 1;
    text-align: center;
    line-height: 40px;
    padding-left: 4px;
  }
}

.error-text {
  color: $basecolor-error;
}

.call-duration-popover-footer {
  padding-top: 6px;
  padding-bottom: 10px;
  background-color: $bgcolor-base;

  .clear-button {
    @include m-fixed-width(96px);
    margin-right: 10px;
  }

  .ok-button {
    @include m-fixed-width(96px);
  }
}
</style>
